// src/components/layout/Header.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const { user, logout, isSystemAdmin } = useAuth();
  const location = useLocation();

  const handleRefresh = () => {
    window.location.reload();
  };

  // SystemAdmin sees Companies management
  // Company users see Dashboard, Configuration, Settings
  const navItems = isSystemAdmin()
    ? [
        { path: '/', label: 'Dashboard' },
        { path: '/companies', label: 'Companies' },
      ]
    : [
        { path: '/', label: 'Dashboard' },
        { path: '/configuration', label: 'Configuration' },
        { path: '/settings', label: 'Settings' },
      ];

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'SystemAdmin':
        return 'bg-purple-100 text-purple-700';
      case 'CompanyAdmin':
        return 'bg-blue-100 text-blue-700';
      case 'CompanyUser':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'SystemAdmin': return 'System Admin';
      case 'CompanyAdmin': return 'Admin';
      case 'CompanyUser': return 'User';
      default: return role;
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">MailSend Dashboard</h1>
              {user?.companyName && (
                <span className="text-sm text-gray-500">{user.companyName}</span>
              )}
              {isSystemAdmin() && (
                <span className="text-sm text-gray-500">All Companies</span>
              )}
            </div>
            <nav className="flex space-x-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium ${
                    location.pathname === item.path
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">
                {user?.displayName || user?.username}
              </span>
              {user?.role && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getRoleBadgeStyle(user.role)}`}>
                  {getRoleLabel(user.role)}
                </span>
              )}
            </div>
            <button
              onClick={handleRefresh}
              className="text-gray-600 hover:text-gray-900"
              title="Refresh"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={logout}
              className="text-gray-600 hover:text-gray-900 flex items-center space-x-2"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
